/**
 * TanStack Query hooks -- the only way a component reads server state.
 *
 * ADR-14: one fetching idiom for the whole application. No second data library
 * and no global store, in this feature or any later one.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import {
  api,
  type ArtifactDetail,
  type IssueAccessPasswordRequest,
  type ArtifactListOwnerType,
  type ArtifactMetadataUpdate,
  type ArtifactOwnerType,
  type NewArtifact,
  type AuditActorQuery,
  type AuditLogQuery,
  type BudgetImportKind,
  type BusinessUnitGroup,
  type BusinessUnitGroupCreate,
  type BusinessUnitGroupDeactivate,
  type BusinessUnitGroupUpdate,
  type BusinessUnit,
  type BusinessUnitCreate,
  type BusinessUnitDeactivate,
  type BusinessUnitUpdate,
  type Client,
  type ClientAnnualBudgetSave,
  type ClientCreate,
  type ClientDeactivate,
  type ClientUpdate,
  type CountryCreate,
  type CreateCycle,
  type CycleListQuery,
  type CycleReopen,
  type ReleaseCycle,
  type CycleTransition,
  type UnitMonthlyBudgetSave,
  type StepChange,
  type DeliveryRoleAssignmentCreate,
  type CountryUpdate,
  type Engagement,
  type EngagementCreate,
  type EngagementDeactivate,
  type EngagementElement,
  type EngagementUpdate,
  type IngestionBatchQuery,
  type JobQuery,
  type LookupValueCreate,
  type LookupValueUpdate,
  type ResourceBuMappingCreate,
  type RoleHolderResolution,
  type ResourceBuMappingUpdate,
  type SettingChange,
  type UserInvite,
  type UserUpdate,
  type AccountDecisionBody,
  type BulkDecisionBody,
  type BillableDaysDecisionBody,
  type KimbleResourceBuDecisionBody,
  type KimbleUnitDecisionBody,
  type SalesRateBuDecisionBody,
  type SalesRateDecideAllBody,
  type SalesRateElementDecisionBody,
  type ElementDecisionBody,
  type EngagementDecisionBody,
} from './client';
import { queryKeys } from './queryKeys';

/** Entries per audit-log page. Within the server's 1-200 range. */
export const AUDIT_LOG_PAGE_SIZE = 50;

/** The audit-log filters: everything the list takes except paging. */
export type AuditLogFilterQuery = Omit<AuditLogQuery, 'cursor' | 'limit'>;

/**
 * The caller's session and permitted navigation.
 *
 * Fetched once at startup and held by `SessionScopeProvider`. Retried
 * deliberately: it gates the entire shell, so a single cold-start blip from a
 * backend that is still migrating should not leave the user staring at an
 * error.
 */
export function useSessionQuery() {
  return useQuery({
    queryKey: queryKeys.session(),
    queryFn: api.getSession,
    retry: 2,
    // The session changes when an Admin grants a role, not on a timer.
    staleTime: 5 * 60 * 1000,
  });
}

/** Platform health. Used by the shell to show the API as unreachable. */
export function useHealthQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.health(),
    queryFn: api.getHealth,
    enabled: options?.enabled ?? true,
    retry: false,
    staleTime: 30 * 1000,
  });
}

/**
 * The audit log, newest first, one keyset page at a time (f1.5, ADR-14).
 *
 * An infinite query: each page's `next_cursor` is the next page param, and
 * `undefined` when `has_more` is false ends the chain. There is no page
 * number and no total to ask for -- the server has neither.
 *
 * `staleTime: Infinity` is deliberate. An audit log that reshuffles itself
 * while an Admin is reading an investigation is worse, not better; fresh
 * entries are fetched by returning to the newest page, never behind the
 * reader's back.
 */
export function useAuditLogQuery(
  filters: AuditLogFilterQuery,
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: queryKeys.auditLog.list(filters),
    queryFn: ({ pageParam }) =>
      api.listAuditLog({ ...filters, cursor: pageParam, limit: AUDIT_LOG_PAGE_SIZE }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined,
    enabled: options?.enabled ?? true,
    staleTime: Infinity,
  });
}

/**
 * The actors the log recorded in a date range, for the filter control.
 *
 * Keyed on the RANGE alone, not on the whole filter set: the options a reader
 * picks from must not shrink to the actors they have already chosen, which is
 * exactly what would happen if this refetched under the actor filter it
 * feeds.
 */
export function useAuditActorsQuery(
  range: AuditActorQuery,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.auditLog.actors(range),
    queryFn: () => api.listAuditActors(range),
    enabled: options?.enabled ?? true,
    staleTime: Infinity,
  });
}

/**
 * One entry with its before/after documents, fetched when a row is expanded.
 *
 * Entries are immutable by privilege -- the runtime role cannot update or
 * delete one -- so a fetched detail never goes stale.
 */
export function useAuditLogEntryQuery(entryId: string) {
  return useQuery({
    queryKey: queryKeys.auditLog.detail(entryId),
    queryFn: () => api.getAuditLogEntry(entryId),
    staleTime: Infinity,
  });
}

/**
 * The settings catalogue (f2.1): every active setting, grouped by section.
 *
 * Not refetched behind the Admin's back: a form whose values change under the
 * cursor is worse than a stale one, and the Admin's own save replaces the
 * cache from its response.
 */
export function useSettingsCatalog() {
  return useQuery({
    queryKey: queryKeys.settings.catalog(),
    queryFn: api.getSettings,
    staleTime: Infinity,
  });
}

/**
 * Save a batch of changed settings, all or nothing.
 *
 * Sends ONLY the keys that changed. On success the server returns the full
 * updated catalogue, which replaces the cache directly -- no second round trip,
 * and the form never works on a stale document after its own save. A `400`
 * arrives as an `ApiError` whose `errors` name each rejected key.
 */
export function useSaveSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (changes: SettingChange[]) => api.updateSettings({ changes }),
    onSuccess: (catalog) => {
      queryClient.setQueryData(queryKeys.settings.catalog(), catalog);
    },
  });
}

/**
 * Reference data (f2.2): three collections, three keys, three pairs of hooks.
 *
 * Each mutation invalidates ONLY its own collection (adr-14): adding a country
 * cannot stale the lookup catalogue, and a screen showing all three refetches
 * exactly the one that changed. A refused write leaves the cache untouched,
 * so the dialog can stay open over data that still matches the server.
 */
export function useLookupCatalog(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.referenceData.lookups(),
    queryFn: api.getLookups,
    staleTime: Infinity,
    /* `enabled` so a screen that has already been refused its area does not
       fire a second request the server will refuse again (f4.2). */
    enabled: options?.enabled ?? true,
  });
}

export function useAddLookupValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ typeCode, value }: { typeCode: string; value: LookupValueCreate }) =>
      api.addLookupValue(typeCode, value),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.referenceData.lookups() });
    },
  });
}

export function useUpdateLookupValue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      typeCode,
      valueId,
      change,
    }: {
      typeCode: string;
      valueId: string;
      change: LookupValueUpdate;
    }) => api.updateLookupValue(typeCode, valueId, change),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.referenceData.lookups() });
    },
  });
}

export function useCountries() {
  return useQuery({
    queryKey: queryKeys.referenceData.countries(),
    queryFn: api.getCountries,
    staleTime: Infinity,
  });
}

export function useAddCountry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (country: CountryCreate) => api.addCountry(country),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.referenceData.countries() });
    },
  });
}

export function useUpdateCountry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ countryId, change }: { countryId: string; change: CountryUpdate }) =>
      api.updateCountry(countryId, change),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.referenceData.countries() });
    },
  });
}

/**
 * The user roster (f3.1). Platform Admin only, enforced server-side.
 *
 * Read whole, because the server returns it whole: there is no cursor to
 * follow and no `has_more` to check. Search, the tab filters and the sort all
 * run client-side over this one result, as f2.2's Resource BU mapping tab does.
 *
 * Not `staleTime: Infinity` like the reference-data reads: an Admin who grants
 * a role, switches tab and comes back should see the grant, and the roster is
 * small enough that refetching costs nothing.
 */
export function useUsers() {
  return useQuery({
    queryKey: queryKeys.users.all(),
    queryFn: api.getUsers,
  });
}

/**
 * Add an account for somebody who has never signed in (f3.2).
 *
 * The endpoint is the server's `POST /users`, which it still documents as an
 * invite. Nothing is SENT to the person -- ADR-5 removed the invitation
 * e-mail, and there is no token, no expiry and no accept route -- so the
 * screen calls it what it is: adding an account, which then waits for that
 * person's first Entra sign-in to claim it.
 *
 * Not optimistic, for f3.1's reason: the server may refuse on e-mail
 * uniqueness after the client believed the row valid, and a row that appears
 * and then vanishes is worse than a brief spinner.
 */
export function useAddUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (account: UserInvite) => api.inviteUser(account),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    },
  });
}

/**
 * Grant or revoke a role, activate or deactivate. One mutation, one key.
 *
 * Deliberately NOT optimistic. The server may refuse with
 * `LAST_ADMIN_REQUIRED` or `CANNOT_MODIFY_OWN_ACCOUNT` after the client
 * believed the change valid, and a row that flicks back is a worse experience
 * than a brief spinner on a screen used a few times a week. A refused write
 * leaves the cache untouched, so the dialog stays open over data that still
 * matches the server.
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, change }: { userId: string; change: UserUpdate }) =>
      api.updateUser(userId, change),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
      // A grant changes what the AFFECTED user may see -- but it can also
      // change what the CALLER sees if an Admin ever administers their own
      // account. The server refuses that outright, so the session is left
      // alone rather than refetched on every grant.
    },
  });
}

export function useResourceBuMappings() {
  return useQuery({
    queryKey: queryKeys.referenceData.resourceBuMappings(),
    queryFn: api.getResourceBuMappings,
    staleTime: Infinity,
  });
}

export function useAddResourceBuMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (mapping: ResourceBuMappingCreate) => api.addResourceBuMapping(mapping),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.referenceData.resourceBuMappings(),
      });
    },
  });
}

export function useUpdateResourceBuMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      mappingId,
      change,
    }: {
      mappingId: string;
      change: ResourceBuMappingUpdate;
    }) => api.updateResourceBuMapping(mappingId, change),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.referenceData.resourceBuMappings(),
      });
    },
  });
}

/** The Organization Overview: one scoped read that every layout projects. */
export function useOrganizationOverview(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.organizationOverview(),
    queryFn: api.getOrganizationOverview,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Business Unit Groups (f4.1). One collection key, five hooks.
 *
 * **Deliberately NOT optimistic**, following f3.1 -- and here the server
 * refusing something the client believed valid is the ROUTINE case, not the
 * edge one: a deactivation is expected to come back as a `409` listing the
 * children, and the dialog then renders what the server said. A row that
 * flicked to Inactive and back would be actively misleading.
 *
 * Every mutation invalidates only this collection, so nothing else on screen
 * refetches. A refused write leaves the cache untouched, which is what lets a
 * dialog stay open over data that still matches the server.
 */
export function useBusinessUnitGroups(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.businessUnitGroups.all(),
    queryFn: api.getBusinessUnitGroups,
    enabled: options?.enabled ?? true,
  });
}

export function useBusinessUnitGroup(groupId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.businessUnitGroups.detail(groupId),
    queryFn: () => api.getBusinessUnitGroup(groupId),
    enabled: options?.enabled ?? true,
  });
}

function useGroupMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<BusinessUnitGroup>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (group) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.businessUnitGroups.all(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.businessUnitGroups.detail(group.id),
      });
    },
  });
}

export function useAddBusinessUnitGroup() {
  return useGroupMutation((group: BusinessUnitGroupCreate) =>
    api.addBusinessUnitGroup(group),
  );
}

export function useUpdateBusinessUnitGroup() {
  return useGroupMutation(
    ({ groupId, change }: { groupId: string; change: BusinessUnitGroupUpdate }) =>
      api.updateBusinessUnitGroup(groupId, change),
  );
}

export function useDeactivateBusinessUnitGroup() {
  return useGroupMutation(
    ({ groupId, body }: { groupId: string; body: BusinessUnitGroupDeactivate }) =>
      api.deactivateBusinessUnitGroup(groupId, body),
  );
}

export function useReactivateBusinessUnitGroup() {
  return useGroupMutation((groupId: string) => api.reactivateBusinessUnitGroup(groupId));
}

/**
 * Business Units (f5.1). One collection key, five hooks, and the same refusal
 * to be optimistic that f3.1 and f4.1 made -- for the same reason: the server
 * refusing something the client believed valid is the ROUTINE case here, not
 * the edge one. A duplicate code within a group, a name already taken in
 * another group, a spelling that already resolves elsewhere and a deactivation
 * with active children are all `409`s the dialog renders.
 *
 * Every unit mutation invalidates the GROUPS collection as well, because
 * `business_unit_count` is rendered there and would otherwise go stale.
 */
export function useBusinessUnits(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.businessUnits.all(),
    queryFn: api.getBusinessUnits,
    enabled: options?.enabled ?? true,
  });
}

export function useBusinessUnit(unitId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.businessUnits.detail(unitId),
    queryFn: () => api.getBusinessUnit(unitId),
    enabled: options?.enabled ?? true,
  });
}

function useUnitMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<BusinessUnit>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (unit) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.businessUnits.all() });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.businessUnits.detail(unit.id),
      });
      // f4.1's group list and detail both carry `business_unit_count`, which
      // this write may have changed. Invalidating it is not tidiness: without
      // it, deactivating the last unit under a group leaves that group showing
      // a count of one until an unrelated action happens to refetch.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.businessUnitGroups.all(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.businessUnitGroups.detail(unit.business_unit_group.id),
      });
    },
  });
}

export function useAddBusinessUnit() {
  return useUnitMutation((unit: BusinessUnitCreate) => api.addBusinessUnit(unit));
}

export function useUpdateBusinessUnit() {
  return useUnitMutation(
    ({ unitId, change }: { unitId: string; change: BusinessUnitUpdate }) =>
      api.updateBusinessUnit(unitId, change),
  );
}

export function useDeactivateBusinessUnit() {
  return useUnitMutation(
    ({ unitId, body }: { unitId: string; body: BusinessUnitDeactivate }) =>
      api.deactivateBusinessUnit(unitId, body),
  );
}

export function useReactivateBusinessUnit() {
  return useUnitMutation((unitId: string) => api.reactivateBusinessUnit(unitId));
}

/**
 * Clients (f6.1) -- the third level, and the hub three later phases open.
 *
 * The same ONE-collection shape as the two levels above. The collection takes
 * no parameter of any kind: 39 rows, and the screen's page controls, search
 * box and four selects all run client-side over the loaded set.
 */
export function useClients(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.clients.all(),
    queryFn: api.getClients,
    enabled: options?.enabled ?? true,
  });
}

export function useClient(clientId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.clients.detail(clientId),
    queryFn: () => api.getClient(clientId),
    enabled: (options?.enabled ?? true) && Boolean(clientId),
  });
}

function useClientMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<Client>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (client) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.clients.all() });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.clients.detail(client.id),
      });
      /*
       * f5.1's unit list and detail both carry `client_count`, which f6.1
       * delivered and this write may have changed. Invalidating it is not
       * tidiness: without it, deactivating the last client under a unit leaves
       * that unit showing a count of one until an unrelated action happens to
       * refetch.
       */
      void queryClient.invalidateQueries({
        queryKey: queryKeys.businessUnits.all(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.businessUnits.detail(client.business_unit.id),
      });
    },
  });
}

export function useAddClient() {
  return useClientMutation((client: ClientCreate) => api.addClient(client));
}

export function useUpdateClient() {
  return useClientMutation(
    ({ clientId, change }: { clientId: string; change: ClientUpdate }) =>
      api.updateClient(clientId, change),
  );
}

export function useDeactivateClient() {
  return useClientMutation(
    ({ clientId, body }: { clientId: string; body: ClientDeactivate }) =>
      api.deactivateClient(clientId, body),
  );
}

export function useReactivateClient() {
  return useClientMutation((clientId: string) => api.reactivateClient(clientId));
}

/**
 * Engagements (f7.1) -- the fourth level, and the first with no screen.
 *
 * The collection is keyed by CLIENT because the server has no global one: the
 * tab renders one client's engagements, and no screen lists them across
 * clients. It takes no parameter of any kind -- engagements per client run 1
 * to 7 -- so the tab's search box and its two selects run client-side.
 *
 * **Deliberately NOT optimistic**, following f3.1, f4.1, f5.1 and f6.1, and
 * here the server refusing something the client believed valid is the ROUTINE
 * case: deactivating an engagement with active elements is expected to come
 * back as a `409` listing them, which the dialog then renders.
 */
/** f11.4: the Commitments tab. Read only; edits happen in the forecast grid. */
export function useClientCommitments(clientId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.clients.commitments(clientId),
    queryFn: () => api.getClientCommitments(clientId),
    enabled: (options?.enabled ?? true) && Boolean(clientId),
  });
}

export function useClientEngagements(
  clientId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.engagements.forClient(clientId),
    queryFn: () => api.getClientEngagements(clientId),
    enabled: (options?.enabled ?? true) && Boolean(clientId),
  });
}

export function useEngagement(
  engagementId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.engagements.detail(engagementId),
    queryFn: () => api.getEngagement(engagementId),
    enabled: (options?.enabled ?? true) && Boolean(engagementId),
  });
}

/**
 * One engagement's delivery elements, fetched when its row is EXPANDED.
 *
 * `enabled` is how "on expansion" is expressed: the query does not fire until
 * the row is open, which is what keeps the common case -- a tab nobody has
 * expanded -- at one request rather than eight.
 */
export function useEngagementElements(
  engagementId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.engagements.elements(engagementId),
    queryFn: () => api.getEngagementElements(engagementId),
    enabled: (options?.enabled ?? true) && Boolean(engagementId),
  });
}

/**
 * Every engagement write invalidates three things, and the third is the one
 * that is easy to forget.
 *
 * Its own collection and its own detail, obviously -- and the CLIENTS
 * collection and detail, because f7.1 delivered f6.1's deferred
 * `engagement_count`. Without that, creating an engagement leaves the Clients
 * list and this tab's own count badge showing yesterday's number until an
 * unrelated action happens to refetch.
 */
function useEngagementMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<Engagement>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (engagement) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.engagements.forClient(engagement.client.id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.engagements.detail(engagement.id),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.clients.all() });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.clients.detail(engagement.client.id),
      });
    },
  });
}

export function useAddEngagement() {
  return useEngagementMutation((engagement: EngagementCreate) =>
    api.addEngagement(engagement),
  );
}

export function useUpdateEngagement() {
  return useEngagementMutation(
    ({
      engagementId,
      change,
    }: {
      engagementId: string;
      change: EngagementUpdate;
    }) => api.updateEngagement(engagementId, change),
  );
}

export function useDeactivateEngagement() {
  return useEngagementMutation(
    ({
      engagementId,
      body,
    }: {
      engagementId: string;
      body: EngagementDeactivate;
    }) => api.deactivateEngagement(engagementId, body),
  );
}

export function useReactivateEngagement() {
  return useEngagementMutation((engagementId: string) =>
    api.reactivateEngagement(engagementId),
  );
}

/**
 * The three element verbs: confirm, deactivate and reactivate.
 *
 * There is **no add**. Delivery elements are discovered from the Kimble feed,
 * the server exposes no create, and f10.4 owns the insert -- so there is
 * nothing here for a component to call.
 *
 * Each invalidates the element list AND the engagement collection, because
 * `element_count` counts active, confirmed elements and every one of the three
 * verbs can change it.
 */
function useElementMutation<TVariables extends { engagementId: string }>(
  mutationFn: (variables: TVariables) => Promise<EngagementElement>,
  clientIdOf: (variables: TVariables) => string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (_element, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.engagements.elements(variables.engagementId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.engagements.forClient(clientIdOf(variables)),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.engagements.detail(variables.engagementId),
      });
    },
  });
}

/** What every element verb needs: which element, on which engagement, whose
 *  client's collection carries the count it changes. */
interface ElementTarget {
  engagementId: string;
  elementId: string;
  clientId: string;
}

export function useConfirmElement() {
  return useElementMutation(
    ({ engagementId, elementId }: ElementTarget) =>
      api.confirmElement(engagementId, elementId),
    (target) => target.clientId,
  );
}

export function useDeactivateElement() {
  return useElementMutation(
    ({ engagementId, elementId }: ElementTarget) =>
      api.deactivateElement(engagementId, elementId),
    (target) => target.clientId,
  );
}

export function useReactivateElement() {
  return useElementMutation(
    ({ engagementId, elementId }: ElementTarget) =>
      api.reactivateElement(engagementId, elementId),
    (target) => target.clientId,
  );
}

/**
 * Delivery ownership (f4.2). Who may own a node, and who owns which node.
 *
 * **Deliberately NOT optimistic**, following f3.1 and f4.1 -- and here the
 * server refusing something the client believed valid is a ROUTINE case:
 * naming a second DEM comes back as a `409` that names the current holder,
 * which is an answer the dialog renders rather than a failure. A row that
 * flicked into place and back would be actively misleading.
 *
 * There is no create hook beside the read. Accounts are minted on the Users
 * screen (f3.2) and nowhere else, because an `app_user` row is a claimable
 * sign-in credential -- so the owner picker reads candidates and never adds
 * one.
 */
export function useAssignableUsers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.assignableUsers.all(),
    queryFn: api.getAssignableUsers,
    enabled: options?.enabled ?? true,
  });
}

/**
 * The owners of ONE node.
 *
 * `nodeType` and `nodeId` are required by the server as selectors, so the
 * key carries both: there is no unqualified ownership collection to cache,
 * and one node's owners are never served for another.
 */
export function useNodeOwners(
  nodeType: string,
  nodeId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.owners.forNode(nodeType, nodeId),
    queryFn: () => api.getOwners(nodeType, nodeId),
    enabled: (options?.enabled ?? true) && Boolean(nodeId),
  });
}

/**
 * Which collection carries a node type's `current_owners` columns.
 *
 * Driven by the node type the caller named rather than by a hardcoded pair of
 * keys, so f6.1 and f7.1 add a line here instead of another `if` inside the
 * mutation. An unrecognised node type invalidates nothing beyond the node's
 * own owners, which is the honest answer: nothing else on screen is known to
 * be stale.
 */
const OWNER_COLUMN_KEYS: Readonly<
  Record<string, { all: () => readonly unknown[]; detail: (id: string) => readonly unknown[] }>
> = {
  BUSINESS_UNIT_GROUP: queryKeys.businessUnitGroups, // f4.1: DEM, CEM
  BUSINESS_UNIT: queryKeys.businessUnits, // f5.1: CM
  CLIENT: queryKeys.clients, // f6.1: DM
  ENGAGEMENT: queryKeys.engagements, // f7.1: PROJECT_HEAD
};

/**
 * Invalidate everything an ownership change can have made stale.
 *
 * Four keys, deliberately: the caller's session (f9.2), the node's own owners,
 * that node's detail, and the collection it belongs to -- whose owner columns come from the server's
 * `current_owners`, so leaving the list alone would show yesterday's owner
 * until something else happened to refetch it. On the Business Units screen
 * that also drives the *"CM not assigned"* badge, which must never disagree
 * with the Ownership card beside it.
 */
function useOwnershipMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
  nodeOf: (variables: TVariables) => { nodeType: string; nodeId: string },
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (_result, variables) => {
      const { nodeType, nodeId } = nodeOf(variables);
      // f9.2: an assignment is what scope is made of, so it can have changed
      // the caller's OWN navigation. The session is the invalidation point;
      // the server's refusal on the next request stays the real guarantee.
      void queryClient.invalidateQueries({ queryKey: queryKeys.session() });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.owners.forNode(nodeType, nodeId),
      });
      // The Organization Overview draws every node's owners from one payload.
      void queryClient.invalidateQueries({ queryKey: queryKeys.organizationOverview() });
      const keys = OWNER_COLUMN_KEYS[nodeType];
      if (!keys) return;
      void queryClient.invalidateQueries({ queryKey: keys.detail(nodeId) });
      void queryClient.invalidateQueries({ queryKey: keys.all() });
    },
  });
}

export function useAssignOwner() {
  return useOwnershipMutation(
    (assignment: DeliveryRoleAssignmentCreate) => api.assignOwner(assignment),
    (assignment) => ({
      nodeType: assignment.node_type,
      nodeId: assignment.node_id,
    }),
  );
}

/**
 * End an assignment. Never a delete: the server exposes none, and the ended
 * row stays readable so the record of who held the role survives.
 */
export function useEndAssignment() {
  return useOwnershipMutation(
    ({ assignmentId }: { assignmentId: string; nodeType: string; nodeId: string }) =>
      api.endAssignment(assignmentId),
    ({ nodeType, nodeId }) => ({ nodeType, nodeId }),
  );
}

/* --- Background jobs (f8.1) -------------------------------------------------

   The first server state in the application that changes WITHOUT anybody
   touching it. Everything above is invalidated by a mutation; a job advances
   on its own, in a worker, and the screen finds out by asking.

   Polling rather than a stream, deliberately. adr-3 gives SSE to f12.2, for a
   live review meeting with people watching; an import is one person waiting
   for a bar to fill, and a two-second poll against a single indexed row is not
   a load problem. Building a second push mechanism here -- before the one that
   needs it exists -- would be infrastructure with no consumer. */

/** How often a running job is re-read. */
export const JOB_POLL_INTERVAL_MS = 2000;

/** The two states after which nothing more will happen, per the server. */
const TERMINAL_JOB_STATES = new Set(['SUCCEEDED', 'FAILED']);

export function isJobTerminal(state: string | undefined): boolean {
  return state !== undefined && TERMINAL_JOB_STATES.has(state);
}

/**
 * One job's progress, polled until it stops moving.
 *
 * `refetchInterval` returns `false` once the state is terminal, so **polling
 * stops without the caller remembering to stop it** -- which is the one thing
 * a progress hook has to get right. A component that unmounts mid-import stops
 * for free; one that stays mounted stops when the job finishes.
 *
 * `enabled` accepts `undefined` for the job id, so a caller that has not
 * uploaded anything yet can call the hook unconditionally rather than breaking
 * the rules of hooks to avoid it.
 */
export function useJobProgress(jobId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.jobs.detail(jobId ?? ''),
    queryFn: () => api.getJob(jobId as string),
    enabled: Boolean(jobId),
    refetchInterval: (query) =>
      isJobTerminal(query.state.data?.state) ? false : JOB_POLL_INTERVAL_MS,
    // A finished job never changes again, so there is nothing to go stale.
    staleTime: 0,
    // A blip mid-import should not blank the bar: the last good reading is a
    // better thing to show than nothing while the next poll is tried.
    retry: 2,
  });
}

/** Recent jobs, newest first. Filters travel in the key (see `queryKeys`). */
export function useJobsQuery(filters: JobQuery = {}) {
  return useQuery({
    queryKey: queryKeys.jobs.list(filters),
    queryFn: () => api.listJobs(filters),
  });
}

/** One ingestion batch: what the file turned out to be, once parsed. */
export function useIngestionBatchQuery(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.ingestionBatches.detail(batchId ?? ''),
    queryFn: () => api.getIngestionBatch(batchId as string),
    enabled: Boolean(batchId),
  });
}

/**
 * Upload a feed.
 *
 * Returns `202` with a job id; the batch is NOT parsed yet, so nothing is
 * invalidated on success except the jobs collection -- a new job now exists
 * and any list of them is stale. The caller passes the returned `job_id` to
 * `useJobProgress` and watches.
 */
export function useUploadBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      sourceSystem,
      datasetName,
    }: {
      file: File;
      sourceSystem: string;
      datasetName?: string;
    }) => api.uploadBatch(file, sourceSystem, datasetName),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all() });
      // A new batch exists, so any list of them is stale -- even though it has
      // not been parsed yet and will render as `RECEIVED`. Showing it at once
      // is what makes "nothing is written on upload" legible rather than
      // merely true: the operator sees the batch appear and nothing else.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.ingestionBatches.all(),
      });
    },
  });
}

/**
 * The recent ingestion batches (f8.2), newest first.
 *
 * Filters travel in the key, as `queryKeys` documents, so a list narrowed to
 * `MASTER_WORKBOOK` is a cache entry of its own rather than a filtered view of
 * one that might have been fetched for another feed.
 */
export function useIngestionBatches(filters: IngestionBatchQuery = {}) {
  return useQuery({
    queryKey: queryKeys.ingestionBatches.list(filters),
    queryFn: () => api.listIngestionBatches(filters),
  });
}

/**
 * One batch's staged plan: what would happen if it were committed.
 *
 * Read from the stored plan, never recomputed -- which is what lets the
 * preview show the operator the same thing f8.3 will commit, rather than a
 * fresh opinion formed after somebody invited a user in another tab.
 */
export function useStagedPlan(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.ingestionBatches.stagedPlan(batchId ?? ''),
    queryFn: () => api.getStagedPlan(batchId as string),
    enabled: Boolean(batchId),
  });
}

/**
 * The slots a human still has to look at.
 *
 * Omitting `states` asks the server for everything that is not `MATCHED`,
 * which is the unresolved list the screen renders. The assign and invite
 * controls are **not** here: f8.3 adds them, and this release deliberately
 * ships the list read-only rather than with buttons that do nothing.
 */
export function useRoleSlots(
  batchId: string | undefined,
  states: readonly string[] = [],
) {
  return useQuery({
    queryKey: queryKeys.ingestionBatches.roleSlots(batchId ?? '', states),
    queryFn: () => api.getRoleSlots(batchId as string, states),
    enabled: Boolean(batchId),
  });
}

/**
 * Abandon a staged batch.
 *
 * The staged rows are **retained** server-side, so the decision stays
 * inspectable; what changes is the batch's state, which is why the batch, its
 * plan and the collection are all invalidated and nothing else is.
 */
export function useDiscardBatch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) => api.discardBatch(batchId),
    onSuccess: (batch) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.ingestionBatches.all(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.ingestionBatches.detail(batch.id),
      });
    },
  });
}

/**
 * Everything one batch's decisions invalidate (f8.3).
 *
 * All three decisions move the same three things: the slots (the decision was
 * recorded), the plan (its `unresolved_slot_count` and its codes changed) and
 * the batch (it may have just reached `STAGED`). Stating that once means a
 * fourth decision cannot forget one of them.
 *
 * **The batch's state is never patched into the cache from a mutation's
 * response.** The server decides whether the last decision has been made, and
 * a refetch is how the screen learns the answer rather than re-deriving it.
 */
function invalidateBatchDecisions(queryClient: QueryClient, batchId: string) {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.ingestionBatches.all(),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.ingestionBatches.detail(batchId),
  });
}

/**
 * Bind one unmatched name to an account -- for every slot it occupies.
 *
 * `kind: INVITE` reaches f3.2's invite service on the server, so a successful
 * resolution has also created an `app_user` row: the users collection is
 * invalidated too, and the Users screen shows the new `invited` account the
 * moment it is looked at.
 *
 * Not optimistic. The server may refuse with `EMAIL_ALREADY_EXISTS` after the
 * client believed the invite valid, and the whole resolution rolls back when
 * it does -- so a row that flicked to *resolved* and back would be a lie the
 * cache told twice.
 */
export function useResolveRoleHolder(batchId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resolution: RoleHolderResolution) =>
      api.resolveRoleHolder(batchId as string, resolution),
    onSuccess: () => {
      invalidateBatchDecisions(queryClient, batchId ?? '');
      void queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.assignableUsers.all() });
    },
  });
}

/** Pick the one holder of a slot the workbook contradicted itself on. */
export function useChooseContestedHolder(batchId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (chosenStagedRoleId: string) =>
      api.chooseContestedHolder(batchId as string, chosenStagedRoleId),
    onSuccess: () => invalidateBatchDecisions(queryClient, batchId ?? ''),
  });
}

/** Replace a proposed code the derivation could not make unique. */
export function useSetStagedNodeCode(batchId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stagedNodeId, code }: { stagedNodeId: string; code: string }) =>
      api.setStagedNodeCode(batchId as string, stagedNodeId, code),
    onSuccess: () => invalidateBatchDecisions(queryClient, batchId ?? ''),
  });
}

/**
 * Commit a staged batch: the hierarchy and every delivery-role assignment, in
 * one transaction.
 *
 * Returns `202` with a job id and writes nothing itself. The caller hands that
 * id to `useJobProgress` -- the hook f8.1 already built and the parse already
 * uses -- rather than polling a second way. Everything the job will write is
 * invalidated on success, because by the time it finishes the hierarchy is no
 * longer what any cached list says it is.
 */
export function useCommitBatch(batchId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.commitBatch(batchId as string),
    onSuccess: () => {
      invalidateBatchDecisions(queryClient, batchId ?? '');
      void queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all() });
    },
  });
}

/* --- COM cycles (f10.2) ------------------------------------------------------ */

/** The cycle list. Any platform role; `capabilities.can_create` gates creating. */
export function useCycles(filters: CycleListQuery) {
  return useQuery({
    queryKey: queryKeys.cycles.list(filters),
    queryFn: () => api.listCycles(filters),
  });
}

/**
 * What a new cycle for this month would derive. The dialog renders this and
 * computes nothing itself: working days, labels and windows are server rules.
 */
export function useCyclePreview(
  period: { year: number; month: number } | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.cycles.preview(period?.year ?? 0, period?.month ?? 0),
    queryFn: () => api.previewCycle(period!.year, period!.month),
    enabled: period !== null && (options?.enabled ?? true),
    retry: false,
  });
}

/** Create a cycle from month and year. Refreshes every cycle query on success. */
export function useCreateCycle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCycle) => api.createCycle(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.cycles.all() });
    },
  });
}

/* --- The cycle workspace and its step rail (f10.3) ---------------------------- */

/** One cycle with its derived block and the transitions available now. */
export function useCycle(cycleId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.detail(cycleId ?? ''),
    queryFn: () => api.getCycle(cycleId as string),
    enabled: Boolean(cycleId),
    retry: false,
  });
}

/**
 * f11.3: what the cycle rail offers the caller for forecast input -- read
 * FIRST by the workspace, which branches on `workflow_access` (never on a
 * client-side role rule).
 */
export function useInputEntry(cycleId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.inputEntry(cycleId ?? ''),
    queryFn: () => api.getCycleInputEntry(cycleId as string),
    enabled: Boolean(cycleId),
    retry: false,
  });
}

/** The six preparation steps. Capabilities are the server's, never re-derived. */
export function useCycleWorkflow(cycleId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.workflow(cycleId ?? ''),
    queryFn: () => api.getCycleWorkflow(cycleId as string),
    enabled: Boolean(cycleId),
    retry: false,
  });
}

function invalidateCycle(queryClient: QueryClient, cycleId: string) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.detail(cycleId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.cycles.all() });
}

/**
 * Accept a step. Success AND a version conflict both refresh the rail: a
 * `409 CYCLE_STEP_VERSION_CONFLICT` means someone else changed the step, and
 * the screen must show what the server holds now.
 */
export function useAcceptCycleStep(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stepCode, body }: { stepCode: string; body: StepChange }) =>
      api.acceptCycleStep(cycleId, stepCode, body),
    onSettled: () => invalidateCycle(queryClient, cycleId),
  });
}

/** Apply a stale step's update: `200` acknowledged, or `202` with a job id. */
export function useApplyCycleStepUpdate(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ stepCode, body }: { stepCode: string; body: StepChange }) =>
      api.applyCycleStepUpdate(cycleId, stepCode, body),
    onSettled: () => invalidateCycle(queryClient, cycleId),
  });
}

/** Take one of f10.1's transitions. The lock is f12.3's `POST …/freeze`, never this. */
export function useTransitionCycle(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CycleTransition) => api.transitionCycle(cycleId, body),
    onSettled: () => invalidateCycle(queryClient, cycleId),
  });
}

/** Reopen a frozen cycle with a reason (f10.1). */
export function useReopenCycle(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CycleReopen) => api.reopenCycle(cycleId, body),
    onSettled: () => invalidateCycle(queryClient, cycleId),
  });
}

/*
 * Step 6 -- release to managers (f10.9). The readiness is always the server's;
 * a release refetches it whatever the outcome, so a `409
 * CYCLE_RELEASE_INCOMPLETE` re-renders the dialog with the fresh list.
 */

export function useCycleReleaseReadiness(cycleId: string) {
  return useQuery({
    queryKey: queryKeys.cycle.releaseReadiness(cycleId),
    queryFn: () => api.getCycleReleaseReadiness(cycleId),
  });
}

export function useReleaseCycle(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReleaseCycle) => api.releaseCycle(cycleId, body),
    // `['cycle', id]` prefixes the workflow and readiness keys, so all refetch.
    onSettled: () => invalidateCycle(queryClient, cycleId),
  });
}

/*
 * Step 1 -- the US Engagements master (f10.4).
 *
 * The batch list, one batch's review, and the step's own job route. Every
 * decision refetches the review rather than patching it in place: figures,
 * the batch state, child cascades and capabilities all move together, and the
 * server is the only place that computes them.
 */

/** Batch states the parse job has not finished with yet. */
const US_ENGAGEMENTS_PARSING_STATES: readonly string[] = ['RECEIVED', 'PARSING'];

export function useUsEngagementsBatches(cycleId: string) {
  return useQuery({
    queryKey: queryKeys.cycle.usEngagements(cycleId),
    queryFn: () => api.listUsEngagementsBatches(cycleId),
    // A batch still being parsed (e.g. after a reload, with no job id in
    // hand) is re-read until the job has moved it on.
    refetchInterval: (query) =>
      query.state.data?.batches.some((batch) =>
        US_ENGAGEMENTS_PARSING_STATES.includes(batch.state.code),
      )
        ? JOB_POLL_INTERVAL_MS
        : false,
  });
}

export function useUsEngagementsReview(cycleId: string, batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.usEngagementsBatch(cycleId, batchId ?? ''),
    queryFn: () => api.getUsEngagementsBatch(cycleId, batchId as string),
    enabled: Boolean(batchId),
  });
}

/** A step-1 parse or commit job, polled until terminal. */
export function useUsEngagementsJob(cycleId: string, jobId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.usEngagementsJob(cycleId, jobId ?? ''),
    queryFn: () => api.getUsEngagementsJob(cycleId, jobId as string),
    enabled: Boolean(jobId),
    refetchInterval: (query) =>
      isJobTerminal(query.state.data?.state) ? false : JOB_POLL_INTERVAL_MS,
    staleTime: 0,
    retry: 2,
  });
}

/** Everything under `['cycle', id, 'us-engagements']`: the list and every review. */
function invalidateUsEngagements(queryClient: QueryClient, cycleId: string) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.usEngagements(cycleId) });
}

export function useUploadUsEngagementsBatch(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadUsEngagementsBatch(cycleId, file),
    onSuccess: () => {
      invalidateUsEngagements(queryClient, cycleId);
      // The upload records activity on step 1 (PENDING -> IN_PROGRESS).
      void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.workflow(cycleId) });
    },
  });
}

export type UsEngagementsDecision =
  | { kind: 'accounts'; itemId: string; body: AccountDecisionBody }
  | { kind: 'engagements'; itemId: string; body: EngagementDecisionBody }
  | { kind: 'elements'; itemId: string; body: ElementDecisionBody };

/**
 * One decision. A `409 ITEM_VERSION_CONFLICT` refreshes too: somebody else
 * decided the item, and the screen must show what the server holds now.
 */
export function useDecideUsEngagementsItem(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, itemId, body }: UsEngagementsDecision) =>
      api.decideUsEngagementsItem(cycleId, batchId, kind, itemId, body),
    onSettled: () => invalidateUsEngagements(queryClient, cycleId),
  });
}

/** Many engagements or elements at once; the server applies all or none. */
export function useDecideUsEngagementsBulk(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkDecisionBody) => api.decideUsEngagementsBulk(cycleId, batchId, body),
    onSettled: () => invalidateUsEngagements(queryClient, cycleId),
  });
}

/** Accept and commit: `202` with the commit job's id; nothing is written here. */
export function useCommitUsEngagementsBatch(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.commitUsEngagementsBatch(cycleId, batchId),
    onSettled: () => invalidateUsEngagements(queryClient, cycleId),
  });
}

export function useDiscardUsEngagementsBatch(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.discardUsEngagementsBatch(cycleId, batchId),
    onSettled: () => invalidateUsEngagements(queryClient, cycleId),
  });
}

/**
 * What a finished step-1 commit job changes: the batch (now `COMMITTED`),
 * the rail (step 1 completed, later steps possibly stale) and the hierarchy
 * the Administration screens read.
 */
export function useRefreshAfterUsEngagementsCommit(cycleId: string) {
  const queryClient = useQueryClient();
  return () => {
    invalidateUsEngagements(queryClient, cycleId);
    invalidateCycle(queryClient, cycleId);
    void queryClient.invalidateQueries({ queryKey: queryKeys.clients.all() });
  };
}

/** After a parse job finishes, the list and the review show the staged rows. */
export function useRefreshUsEngagements(cycleId: string) {
  const queryClient = useQueryClient();
  return () => invalidateUsEngagements(queryClient, cycleId);
}

/* --- f10.5: step 2, billable days -------------------------------------------
 * The same shape as step 1: the batch list, one batch's review, the step's own
 * job route. Every decision refetches the review -- figures, batch state and
 * capabilities move together and only the server computes them.
 */

export function useBillableDaysBatches(cycleId: string) {
  return useQuery({
    queryKey: queryKeys.cycle.billableDays(cycleId),
    queryFn: () => api.listBillableDaysBatches(cycleId),
    refetchInterval: (query) =>
      query.state.data?.batches.some((batch) => US_ENGAGEMENTS_PARSING_STATES.includes(batch.state.code))
        ? JOB_POLL_INTERVAL_MS
        : false,
  });
}

export function useBillableDaysReview(cycleId: string, batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.billableDaysBatch(cycleId, batchId ?? ''),
    queryFn: () => api.getBillableDaysBatch(cycleId, batchId as string),
    enabled: Boolean(batchId),
  });
}

/** A step-2 parse or commit job, polled until terminal. */
export function useBillableDaysJob(cycleId: string, jobId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.billableDaysJob(cycleId, jobId ?? ''),
    queryFn: () => api.getBillableDaysJob(cycleId, jobId as string),
    enabled: Boolean(jobId),
    refetchInterval: (query) => (isJobTerminal(query.state.data?.state) ? false : JOB_POLL_INTERVAL_MS),
    staleTime: 0,
    retry: 2,
  });
}

function invalidateBillableDays(queryClient: QueryClient, cycleId: string) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.billableDays(cycleId) });
}

export function useUploadBillableDaysBatch(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadBillableDaysBatch(cycleId, file),
    onSuccess: () => {
      invalidateBillableDays(queryClient, cycleId);
      // The upload records activity on step 2 (PENDING -> IN_PROGRESS).
      void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.workflow(cycleId) });
    },
  });
}

/** One country decision. A `409 ITEM_VERSION_CONFLICT` refreshes too. */
export function useDecideBillableDaysCountry(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: BillableDaysDecisionBody }) =>
      api.decideBillableDaysCountry(cycleId, batchId, itemId, body),
    onSettled: () => invalidateBillableDays(queryClient, cycleId),
  });
}

export function useCommitBillableDaysBatch(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.commitBillableDaysBatch(cycleId, batchId),
    onSettled: () => invalidateBillableDays(queryClient, cycleId),
  });
}

export function useDiscardBillableDaysBatch(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.discardBillableDaysBatch(cycleId, batchId),
    onSettled: () => invalidateBillableDays(queryClient, cycleId),
  });
}

/** After a parse: the list and review. After a commit: also the rail. */
export function useRefreshBillableDays(cycleId: string) {
  const queryClient = useQueryClient();
  return (committed: boolean) => {
    invalidateBillableDays(queryClient, cycleId);
    if (committed) invalidateCycle(queryClient, cycleId);
  };
}

/* --- f10.8: step 5, exchange rates ------------------------------------------
 * One feed, no decisions: the batch list, one batch's review, the step's own
 * job route, and the cycle's committed rates. Capabilities are the server's.
 */

export function useExchangeRateBatches(cycleId: string) {
  return useQuery({
    queryKey: queryKeys.cycle.exchangeRates(cycleId),
    queryFn: () => api.listExchangeRateBatches(cycleId),
    refetchInterval: (query) =>
      query.state.data?.batches.some((batch) => US_ENGAGEMENTS_PARSING_STATES.includes(batch.state.code))
        ? JOB_POLL_INTERVAL_MS
        : false,
  });
}

export function useExchangeRateReview(cycleId: string, batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.exchangeRateBatch(cycleId, batchId ?? ''),
    queryFn: () => api.getExchangeRateBatch(cycleId, batchId as string),
    enabled: Boolean(batchId),
  });
}

/** A step-5 parse or commit job, polled until terminal. */
export function useExchangeRateJob(cycleId: string, jobId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.exchangeRateJob(cycleId, jobId ?? ''),
    queryFn: () => api.getExchangeRateJob(cycleId, jobId as string),
    enabled: Boolean(jobId),
    refetchInterval: (query) => (isJobTerminal(query.state.data?.state) ? false : JOB_POLL_INTERVAL_MS),
    staleTime: 0,
    retry: 2,
  });
}

/** The cycle's current committed rates (EUR per unit). */
export function useCycleExchangeRates(cycleId: string) {
  return useQuery({
    queryKey: queryKeys.cycle.cycleExchangeRates(cycleId),
    queryFn: () => api.listCycleExchangeRates(cycleId),
  });
}

function invalidateExchangeRates(queryClient: QueryClient, cycleId: string) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.exchangeRates(cycleId) });
}

export function useUploadExchangeRateBatch(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadExchangeRateBatch(cycleId, file),
    onSuccess: () => {
      invalidateExchangeRates(queryClient, cycleId);
      // The upload records activity on step 5 (PENDING -> IN_PROGRESS).
      void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.workflow(cycleId) });
    },
  });
}

export function useCommitExchangeRateBatch(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.commitExchangeRateBatch(cycleId, batchId),
    onSettled: () => invalidateExchangeRates(queryClient, cycleId),
  });
}

export function useDiscardExchangeRateBatch(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.discardExchangeRateBatch(cycleId, batchId),
    onSettled: () => invalidateExchangeRates(queryClient, cycleId),
  });
}

/** After a parse: the list and review. After a commit: also the rail. */
export function useRefreshExchangeRates(cycleId: string) {
  const queryClient = useQueryClient();
  return (committed: boolean) => {
    invalidateExchangeRates(queryClient, cycleId);
    if (committed) invalidateCycle(queryClient, cycleId);
  };
}

/* --- f10.6: step 3, the three Kimble dumps ----------------------------------
 * Scopes, the batch list, one batch's review and the step's own job route.
 * Every decision refetches the review: figures, state and capabilities move
 * together and only the server computes them.
 */

function invalidateKimbleDump(queryClient: QueryClient, cycleId: string) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.kimbleDump(cycleId) });
}

export function useKimbleDumpScopes(cycleId: string) {
  return useQuery({
    queryKey: queryKeys.cycle.kimbleDumpScopes(cycleId),
    queryFn: () => api.listKimbleDumpScopes(cycleId),
  });
}

export function useKimbleDumpBatches(cycleId: string) {
  return useQuery({
    queryKey: queryKeys.cycle.kimbleDump(cycleId),
    queryFn: () => api.listKimbleDumpBatches(cycleId),
    refetchInterval: (query) =>
      query.state.data?.batches.some((batch) => US_ENGAGEMENTS_PARSING_STATES.includes(batch.state.code))
        ? JOB_POLL_INTERVAL_MS
        : false,
  });
}

export function useKimbleDumpReview(cycleId: string, batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.kimbleDumpBatch(cycleId, batchId ?? ''),
    queryFn: () => api.getKimbleDumpBatch(cycleId, batchId as string),
    enabled: Boolean(batchId),
  });
}

/** A step-3 parse or commit job, polled until terminal. */
export function useKimbleDumpJob(cycleId: string, jobId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.kimbleDumpJob(cycleId, jobId ?? ''),
    queryFn: () => api.getKimbleDumpJob(cycleId, jobId as string),
    enabled: Boolean(jobId),
    refetchInterval: (query) => (isJobTerminal(query.state.data?.state) ? false : JOB_POLL_INTERVAL_MS),
    staleTime: 0,
    retry: 2,
  });
}

export function useUploadKimbleDumpBatch(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, scope, businessUnitId }: { file: File; scope: string; businessUnitId?: string }) =>
      api.uploadKimbleDumpBatch(cycleId, file, scope, businessUnitId),
    onSuccess: () => {
      invalidateKimbleDump(queryClient, cycleId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.workflow(cycleId) });
    },
  });
}

/** One Resource BU decision. A `409 ITEM_VERSION_CONFLICT` refreshes too. */
export function useDecideKimbleResourceBu(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: KimbleResourceBuDecisionBody }) =>
      api.decideKimbleResourceBu(cycleId, batchId, itemId, body),
    onSettled: () => invalidateKimbleDump(queryClient, cycleId),
  });
}

export function useDecideKimbleUnit(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: KimbleUnitDecisionBody }) =>
      api.decideKimbleUnit(cycleId, batchId, itemId, body),
    onSettled: () => invalidateKimbleDump(queryClient, cycleId),
  });
}

export function useCommitKimbleDumpBatch(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.commitKimbleDumpBatch(cycleId, batchId),
    onSettled: () => invalidateKimbleDump(queryClient, cycleId),
  });
}

export function useDiscardKimbleDumpBatch(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.discardKimbleDumpBatch(cycleId, batchId),
    onSettled: () => invalidateKimbleDump(queryClient, cycleId),
  });
}

/** After a parse: scopes, list and review. After a commit: also the rail. */
export function useRefreshKimbleDump(cycleId: string) {
  const queryClient = useQueryClient();
  return (committed: boolean) => {
    invalidateKimbleDump(queryClient, cycleId);
    if (committed) invalidateCycle(queryClient, cycleId);
  };
}

/* --- f10.7: step 4, the three COSMA sales-rate reports ----------------------
 * Scopes, the batch list, one batch's review and the step's own job route.
 * Every decision refetches the review: keys, figures, state and capabilities
 * move together and only the server computes them.
 */

function invalidateSalesRates(queryClient: QueryClient, cycleId: string) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.salesRates(cycleId) });
}

export function useSalesRateScopes(cycleId: string) {
  return useQuery({
    queryKey: queryKeys.cycle.salesRateScopes(cycleId),
    queryFn: () => api.listSalesRateScopes(cycleId),
  });
}

export function useSalesRateBatches(cycleId: string) {
  return useQuery({
    queryKey: queryKeys.cycle.salesRates(cycleId),
    queryFn: () => api.listSalesRateBatches(cycleId),
    refetchInterval: (query) =>
      query.state.data?.batches.some((batch) => US_ENGAGEMENTS_PARSING_STATES.includes(batch.state.code))
        ? JOB_POLL_INTERVAL_MS
        : false,
  });
}

export function useSalesRateReview(cycleId: string, batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.salesRateBatch(cycleId, batchId ?? ''),
    queryFn: () => api.getSalesRateBatch(cycleId, batchId as string),
    enabled: Boolean(batchId),
  });
}

/** A step-4 parse or commit job, polled until terminal. */
export function useSalesRateJob(cycleId: string, jobId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.cycle.salesRateJob(cycleId, jobId ?? ''),
    queryFn: () => api.getSalesRateJob(cycleId, jobId as string),
    enabled: Boolean(jobId),
    refetchInterval: (query) => (isJobTerminal(query.state.data?.state) ? false : JOB_POLL_INTERVAL_MS),
    staleTime: 0,
    retry: 2,
  });
}

export function useUploadSalesRateBatch(cycleId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, scope }: { file: File; scope: string }) => api.uploadSalesRateBatch(cycleId, file, scope),
    onSuccess: () => {
      invalidateSalesRates(queryClient, cycleId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.cycle.workflow(cycleId) });
    },
  });
}

/** One Resource BU decision. A `409 ITEM_VERSION_CONFLICT` refreshes too. */
export function useDecideSalesRateResourceBu(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: SalesRateBuDecisionBody }) =>
      api.decideSalesRateResourceBu(cycleId, batchId, itemId, body),
    onSettled: () => invalidateSalesRates(queryClient, cycleId),
  });
}

export function useDecideSalesRateElement(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: SalesRateElementDecisionBody }) =>
      api.decideSalesRateElement(cycleId, batchId, itemId, body),
    onSettled: () => invalidateSalesRates(queryClient, cycleId),
  });
}

/** Keep all / Exclude all. A `409 BATCH_VERSION_CONFLICT` refreshes too. */
export function useDecideAllSalesRateElements(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SalesRateDecideAllBody) => api.decideAllSalesRateElements(cycleId, batchId, body),
    onSettled: () => invalidateSalesRates(queryClient, cycleId),
  });
}

export function useCommitSalesRateBatch(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.commitSalesRateBatch(cycleId, batchId),
    onSettled: () => invalidateSalesRates(queryClient, cycleId),
  });
}

export function useDiscardSalesRateBatch(cycleId: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.discardSalesRateBatch(cycleId, batchId),
    onSettled: () => invalidateSalesRates(queryClient, cycleId),
  });
}

/** After a parse: scopes, list and review. After a commit: also the rail. */
export function useRefreshSalesRates(cycleId: string) {
  const queryClient = useQueryClient();
  return (committed: boolean) => {
    invalidateSalesRates(queryClient, cycleId);
    if (committed) invalidateCycle(queryClient, cycleId);
  };
}

/* --- f10.10: budget reference data ------------------------------------------
 * Two tables edited in place and a CSV import. Every mutation invalidates the
 * `budgets` prefix; a `409 VERSION_CONFLICT` surfaces as the mutation's error,
 * carrying `current` (adr-14). Nothing here decides who may see budget -- the
 * server answers 403 to anyone but Finance and Admin.
 */

export function useClientAnnualBudgets(fiscalYear: number) {
  return useQuery({
    queryKey: queryKeys.budgets.clientAnnual(fiscalYear),
    queryFn: () => api.listClientAnnualBudgets(fiscalYear),
  });
}

export function useUnitMonthlyBudgets(fiscalYear: number) {
  return useQuery({
    queryKey: queryKeys.budgets.unitMonthly(fiscalYear),
    queryFn: () => api.listUnitMonthlyBudgets(fiscalYear),
  });
}

function invalidateBudgets(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.budgets.all() });
}

export function useSaveClientAnnualBudget(fiscalYear: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, body }: { clientId: string; body: ClientAnnualBudgetSave }) =>
      api.saveClientAnnualBudget(clientId, fiscalYear, body),
    onSettled: () => invalidateBudgets(queryClient),
  });
}

export function useSaveUnitMonthlyBudget(fiscalYear: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ unitId, body }: { unitId: string; body: UnitMonthlyBudgetSave }) =>
      api.saveUnitMonthlyBudget(unitId, fiscalYear, body),
    onSettled: () => invalidateBudgets(queryClient),
  });
}

export function useBudgetImports() {
  return useQuery({
    queryKey: queryKeys.budgets.imports(),
    queryFn: () => api.listBudgetImports(),
    refetchInterval: (query) =>
      query.state.data?.batches.some((batch) => US_ENGAGEMENTS_PARSING_STATES.includes(batch.state.code))
        ? JOB_POLL_INTERVAL_MS
        : false,
  });
}

export function useBudgetImportReview(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.budgets.importBatch(batchId ?? ''),
    queryFn: () => api.getBudgetImport(batchId as string),
    enabled: Boolean(batchId),
  });
}

/** A budget parse or commit job, polled until terminal. */
export function useBudgetImportJob(jobId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.budgets.importJob(jobId ?? ''),
    queryFn: () => api.getBudgetImportJob(jobId as string),
    enabled: Boolean(jobId),
    refetchInterval: (query) => (isJobTerminal(query.state.data?.state) ? false : JOB_POLL_INTERVAL_MS),
    staleTime: 0,
    retry: 2,
  });
}

export function useUploadBudgetImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, file }: { kind: BudgetImportKind; file: File }) => api.uploadBudgetImport(kind, file),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.budgets.imports() }),
  });
}

export function useCommitBudgetImport(batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.commitBudgetImport(batchId),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: queryKeys.budgets.imports() }),
  });
}

export function useDiscardBudgetImport(batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.discardBudgetImport(batchId),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: queryKeys.budgets.imports() }),
  });
}

/** After a parse: the import list and review. After a commit: every figure too. */
export function useRefreshBudgets() {
  const queryClient = useQueryClient();
  return () => invalidateBudgets(queryClient);
}


// --- Artifacts (f15.1) ---------------------------------------------------------

/** The owning node's detail query, whose `artifact_count` feeds the tab badge. */
function ownerDetailKey(ownerType: ArtifactOwnerType, ownerId: string) {
  switch (ownerType) {
    case 'BUSINESS_UNIT_GROUP':
      return queryKeys.businessUnitGroups.detail(ownerId);
    case 'BUSINESS_UNIT':
      return queryKeys.businessUnits.detail(ownerId);
    default:
      // A client, or an engagement whose artifacts count on its client's badge.
      return queryKeys.clients.all();
  }
}

export function useArtifactsForOwner(ownerType: ArtifactListOwnerType, ownerId: string) {
  return useQuery({
    queryKey: queryKeys.artifacts.forOwner(ownerType, ownerId),
    queryFn: () => api.listArtifactsForOwner(ownerType, ownerId),
    enabled: Boolean(ownerId),
  });
}

export function useArtifact(artifactId: string) {
  return useQuery({
    queryKey: queryKeys.artifacts.detail(artifactId),
    queryFn: () => api.getArtifact(artifactId),
    enabled: Boolean(artifactId),
  });
}

/** f15.2: every artifact the caller may see, whatever it hangs off. */
export function useArtifacts() {
  return useQuery({ queryKey: queryKeys.artifacts.list(), queryFn: () => api.listArtifacts() });
}

/** f15.2: the owner picker's nodes for one level (a convenience; the server re-authorizes). */
export function useArtifactOwnerOptions(level: ArtifactOwnerType | '', options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.artifacts.ownerOptions(level),
    queryFn: () => api.listArtifactOwnerOptions(level as ArtifactOwnerType),
    enabled: level !== '' && (options.enabled ?? true),
  });
}

/** Invalidate every artifact list and detail, plus the owning node's badge. */
function useArtifactInvalidation() {
  const queryClient = useQueryClient();
  return (owner: { type: string; id: string }) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.artifacts.all() });
    void queryClient.invalidateQueries({
      queryKey: ownerDetailKey(owner.type as ArtifactOwnerType, owner.id),
    });
  };
}

export function useCreateArtifact() {
  const invalidate = useArtifactInvalidation();
  return useMutation({
    mutationFn: (input: NewArtifact) => api.createArtifact(input),
    onSuccess: (created) => invalidate(created.owner),
  });
}

export function useUploadArtifactVersion(artifact: ArtifactDetail | undefined) {
  const invalidate = useArtifactInvalidation();
  return useMutation({
    mutationFn: ({ file, releaseNote, publish = false }: { file: File; releaseNote?: string; publish?: boolean }) =>
      api.uploadArtifactVersion(artifact?.id ?? '', file, releaseNote, publish),
    onSuccess: () => {
      if (artifact) invalidate(artifact.owner);
    },
  });
}

/** f15.3: move the published pointer; the detail refreshes from the response. */
export function usePublishArtifactVersion(artifact: ArtifactDetail | undefined) {
  const invalidate = useArtifactInvalidation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) => api.publishArtifactVersion(artifact?.id ?? '', versionId),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.artifacts.detail(updated.id), updated);
      invalidate(updated.owner);
    },
  });
}

/** f15.3: make the artifact Private. */
export function useUnpublishArtifact(artifact: ArtifactDetail | undefined) {
  const invalidate = useArtifactInvalidation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) => api.unpublishArtifact(artifact?.id ?? '', reason),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.artifacts.detail(updated.id), updated);
      invalidate(updated.owner);
    },
  });
}

// --- Access codes (f15.4) -------------------------------------------------------

export function useArtifactAccessPasswords(artifactId: string) {
  return useQuery({
    queryKey: queryKeys.artifacts.accessPasswords(artifactId),
    queryFn: () => api.listArtifactAccessPasswords(artifactId),
    enabled: Boolean(artifactId),
  });
}

/**
 * Issue a code. The plaintext is handed to `onPlaintext` (the reveal dialog's
 * local state) and is deliberately NOT the mutation's result, so it never
 * sits in TanStack Query's mutation cache. Invalidating `artifacts.all()`
 * refreshes the code list, the detail and the list's Codes column.
 */
export function useIssueAccessPassword(artifact: ArtifactDetail, onPlaintext: (plaintext: string) => void) {
  const invalidate = useArtifactInvalidation();
  return useMutation({
    mutationFn: async (input: IssueAccessPasswordRequest) => {
      const issued = await api.issueArtifactAccessPassword(artifact.id, input);
      onPlaintext(issued.password);
      return issued.access_password;
    },
    onSuccess: () => invalidate(artifact.owner),
  });
}

/** Revoke ONE code; the others are untouched. */
export function useRevokeAccessPassword(artifact: ArtifactDetail) {
  const invalidate = useArtifactInvalidation();
  return useMutation({
    mutationFn: ({ passwordId, reason }: { passwordId: string; reason: string }) =>
      api.revokeArtifactAccessPassword(artifact.id, passwordId, reason),
    onSuccess: () => invalidate(artifact.owner),
  });
}

// --- Access history (f15.6) ------------------------------------------------------

export const ARTIFACT_ACCESS_PAGE_SIZE = 20;

/**
 * The artifact's access history. Publish/unpublish (f15.3) and issue/revoke
 * (f15.4) invalidate `artifacts.all()`, which prefixes this key. External
 * events arrive without a mutation, so the card offers a manual refresh; there
 * is no polling and no SSE.
 */
export function useArtifactAccessEvents(artifactId: string) {
  return useInfiniteQuery({
    queryKey: queryKeys.artifacts.accessEvents(artifactId),
    queryFn: ({ pageParam }) =>
      api.listArtifactAccessEvents(artifactId, { limit: ARTIFACT_ACCESS_PAGE_SIZE, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.has_more ? (lastPage.next_cursor ?? undefined) : undefined),
    enabled: Boolean(artifactId),
  });
}

export function useUpdateArtifact(artifact: ArtifactDetail | undefined) {
  const invalidate = useArtifactInvalidation();
  return useMutation({
    mutationFn: (update: ArtifactMetadataUpdate) => api.updateArtifact(artifact?.id ?? '', update),
    onSuccess: () => {
      if (artifact) invalidate(artifact.owner);
    },
  });
}
